
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Courses() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState<'math' | 'english'>('math');

  const handleBookClass = () => {
    // Trigger Readdy Agent widget for booking
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    } else {
      // Fallback to contact page
      navigate('/contact');
    }
  };

  const mathPricing = [
    { 
      type: t('courses.oneOnOne'), 
      price: '250 UAH', 
      students: 1, 
      features: [
        t('courses.features.personalApproach'),
        t('courses.features.flexibleSchedule'),
        t('courses.features.individualProgram')
      ]
    },
    { 
      type: t('courses.twoStudents'), 
      price: '175 UAH', 
      students: 2, 
      features: [
        t('courses.features.interactiveLearning'),
        t('courses.features.peerInteraction'),
        t('courses.features.costSaving')
      ]
    },
    { 
      type: t('courses.fourStudents'), 
      price: '100 UAH', 
      students: 4, 
      features: [
        t('courses.features.groupDynamics'),
        t('courses.features.bestPrice'),
        t('courses.features.socialLearning')
      ]
    }
  ];

  const englishPricing = [
    { 
      type: t('courses.twoStudents'), 
      price: '275 UAH', 
      students: 2, 
      features: [
        t('courses.features.conversationPractice'),
        t('courses.features.pairWork'),
        t('courses.features.interactiveTasks')
      ]
    },
    { 
      type: t('courses.fourStudents'), 
      price: '225 UAH', 
      students: 4, 
      features: [
        t('courses.features.groupDiscussions'),
        t('courses.features.rolePlay'),
        t('courses.features.teamwork')
      ]
    },
    { 
      type: t('courses.sixStudents'), 
      price: '175 UAH', 
      students: 6, 
      features: [
        t('courses.features.largeProjects'),
        t('courses.features.presentations'),
        t('courses.features.debates')
      ]
    }
  ];

  const courseFeatures = [
    { 
      icon: 'ri-video-line', 
      title: t('courses.features.onlineLessons'), 
      description: t('courses.features.onlineLessonsDesc')
    },
    { 
      icon: 'ri-book-open-line', 
      title: t('courses.features.materials'), 
      description: t('courses.features.materialsDesc')
    },
    { 
      icon: 'ri-user-star-line', 
      title: t('courses.features.expertTeachers'), 
      description: t('courses.features.expertTeachersDesc')
    },
    { 
      icon: 'ri-calendar-check-line', 
      title: t('courses.features.flexible'), 
      description: t('courses.features.flexibleDesc')
    },
    { 
      icon: 'ri-headphone-line', 
      title: t('courses.features.support'), 
      description: t('courses.features.supportDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Unique Motion Video Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video - Unique for Courses Page */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover object-center"
            poster="https://readdy.ai/api/search-image?query=Bright%20vibrant%20mathematics%20and%20english%20learning%20scene%20with%20diverse%20students%20studying%20online%20with%20expert%20teachers%2C%20modern%20educational%20technology%20setup%20with%20mathematical%20equations%20and%20english%20books%20visible%2C%20professional%20bright%20classroom%20environment%20with%20natural%20lighting%2C%20students%20engaged%20in%20interactive%20learning%20sessions%20with%20tablets%20and%20computers%2C%20colorful%20educational%20atmosphere%20with%20emerald%20green%20and%20teal%20accents%2C%20high%20quality%20professional%20educational%20excellence&width=1920&height=1080&seq=courses-unique-video-poster&orientation=landscape"
          >
            <source src="https://videos.pexels.com/video-files/8088495/8088495-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/8088495/8088495-hd_1920_1080_25fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/8088495/8088495-sd_640_360_25fps.mp4" type="video/mp4" />
          </video>
          {/* High contrast overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/40 via-emerald-800/30 to-teal-900/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight drop-shadow-2xl">
              <span className="bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent">
                {t('courses.title')}
              </span>
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-12 font-medium leading-relaxed drop-shadow-xl max-w-4xl mx-auto">
              {t('courses.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Button 
                size="lg" 
                className="px-12 py-6 text-xl font-bold border-2 border-white/30 rounded-xl"
                onClick={handleBookClass}
              >
                <i className="ri-user-add-line mr-3 text-xl"></i>
                {t('cta.enroll')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Course Selection */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-full p-2 shadow-xl border-2 border-emerald-100">
              <button
                onClick={() => setSelectedCourse('math')}
                className={`px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 hover:shadow-xl active:scale-95 active:translate-y-0 ${
                  selectedCourse === 'math' 
                    ? 'bg-emerald-600 text-white shadow-lg transform scale-105' 
                    : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                <i className="ri-calculator-line mr-2"></i>
                {t('courses.mathematics')}
              </button>
              <button
                onClick={() => setSelectedCourse('english')}
                className={`px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:-translate-y-2 hover:shadow-xl active:scale-95 active:translate-y-0 ${
                  selectedCourse === 'english' 
                    ? 'bg-teal-600 text-white shadow-lg transform scale-105' 
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                <i className="ri-translate-2 mr-2"></i>
                {t('courses.english')}
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {(selectedCourse === 'math' ? mathPricing : englishPricing).map((plan, index) => (
              <div key={index} className={`bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 border-2 ${
                selectedCourse === 'math' ? 'border-emerald-100 hover:border-emerald-200' : 'border-teal-100 hover:border-teal-200'
              }`}>
                <div className="text-center mb-8">
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 shadow-lg ${
                    selectedCourse === 'math' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-teal-500 to-teal-600'
                  }`}>
                    <i className={`ri-group-line text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.type}</h3>
                  <div className={`text-4xl font-bold mb-2 ${
                    selectedCourse === 'math' ? 'text-emerald-600' : 'text-teal-600'
                  }`}>
                    {plan.price}
                  </div>
                  <p className="text-gray-600 text-lg">{t('courses.perStudent')} {t('courses.perClass')}</p>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-4 shadow-md ${
                        selectedCourse === 'math' ? 'bg-emerald-100' : 'bg-teal-100'
                      }`}>
                        <i className={`ri-check-line text-sm font-bold ${
                          selectedCourse === 'math' ? 'text-emerald-600' : 'text-teal-600'
                        }`}></i>
                      </div>
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full py-4 text-lg font-bold ${
                    selectedCourse === 'math' 
                      ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' 
                      : 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800'
                  }`}
                  onClick={handleBookClass}
                >
                  <i className="ri-user-add-line mr-3"></i>
                  {t('cta.enroll')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              {t('courses.whatIncludes')}
            </h2>
            <p className="text-xl text-gray-600 font-medium">
              {t('courses.everythingNeeded')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courseFeatures.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 border border-emerald-100 hover:border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('courses.readyToStart')}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {t('courses.chooseAndStart')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-100"
              onClick={handleBookClass}
            >
              <i className="ri-calendar-check-line mr-3"></i>
              {t('cta.bookClass')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-emerald-600"
              onClick={() => navigate('/contact')}
            >
              <i className="ri-customer-service-2-line mr-3"></i>
              {t('cta.contact')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
